import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Breadcrumb, Layout, theme, Alert, Spin, Empty, Row, Col, Tag, Button, Table, Select, Space, Typography, Collapse, Badge } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useCalculateBrandEquityMutation, useGetBrandsQuery, useGetCohortsByBrandQuery } from "../services/nestjs-base-api";
import { IBrandEquityScoreResponse, PeriodEnum } from "../services/nestjs-base-api/types";
import { useElementAccess } from "../hooks/useTierAccess";
import { ThunderboltOutlined, WarningOutlined, AppstoreOutlined, UnorderedListOutlined, LockOutlined, CrownOutlined, CommentOutlined, TrophyOutlined, ExclamationCircleOutlined, FireOutlined } from "@ant-design/icons";
import { Scale, BicepsFlexed } from 'lucide-react';
import { StatementCard, StatementData } from "../components/StatementCards";
import { ScoreDistributionModal } from "../components/ScoreDistributionModal";
import { DataSummaryCard, useDataSummary } from "../components/BrandEquity/DataSummaryCard";
import { getDisplayName } from "../utils/elementDisplayNames";
import { MaturityTag } from "../components/MaturityTag";
import { MaturityAlert } from "../components/MaturityAlert";

const { Content } = Layout;
const { Title, Text } = Typography;

const StatementsPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();

    // Element access for subscription tier restrictions
    const { isElementAllowed, hasUnrestrictedAccess } = useElementAccess();

    // Check if a question category is locked
    const isCategoryLocked = useCallback((categoryName: string) => {
        if (hasUnrestrictedAccess) return false;
        return !isElementAllowed(categoryName, 'questionCategory');
    }, [hasUnrestrictedAccess, isElementAllowed]);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    
    const [brandEquityData, setBrandEquityData] = useState<IBrandEquityScoreResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [sortBy, setSortBy] = useState<'default' | 'meanScore' | 'meanIntensityPercentage' | 'meanBrandPowerPercentage' | 'meanControversyPercentage'>('default');
    const [calculateBrandEquity] = useCalculateBrandEquityMutation();

    // Load brands for display
    const { data: brandsData } = useGetBrandsQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: user?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !user?.organisation?.id }
    );

    // Get filter info to determine which brand's cohorts to load
    const getFilterInfo = () => {
        if (!user?.id) return null;
        const filterKey = `brandEquityFilters_${user.id}`;
        const savedFilters = localStorage.getItem(filterKey);
        if (!savedFilters) return null;
        try {
            return JSON.parse(savedFilters);
        } catch (e) {
            return null;
        }
    };

    const filterInfo = getFilterInfo();

    // Load cohorts for the selected brand
    const { data: cohortsData } = useGetCohortsByBrandQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: user?.organisation?.id || 0,
            brandId: filterInfo?.brandId || 0
        },
        { skip: !user?.jwtToken || !user?.organisation?.id || !filterInfo?.brandId }
    );

    // Score Distribution Modal state
    const [scoreModalVisible, setScoreModalVisible] = useState(false);
    const [selectedStatement, setSelectedStatement] = useState<StatementData | null>(null);
    
    const handleResponseCountClick = useCallback((statement: StatementData) => {
        setSelectedStatement(statement);
        setScoreModalVisible(true);
    }, []);
    
    const handleCloseScoreModal = useCallback(() => {
        setScoreModalVisible(false);
        setSelectedStatement(null);
    }, []);
    
    const loadBrandEquityData = useCallback(async () => {
        if (!user?.organisation?.id || !user?.jwtToken) return;
        
        try {
            setIsLoading(true);
            setError(null);
            
            // Check for cached data first
            const cacheKey = `brandEquityResults_${user.id}`;
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                // Use cache if less than 5 minutes old
                if (Date.now() - timestamp < 5 * 60 * 1000) {
                    setBrandEquityData(data);
                    setIsLoading(false);
                    return;
                }
            }
            
            // Get filters from localStorage (same as Data Selection page)
            const filterKey = `brandEquityFilters_${user.id}`;
            const savedFilters = localStorage.getItem(filterKey);
            let filters: { brandIds?: number[], dateFrom?: string, dateTo?: string, cohortIds?: number[], countryCodes?: string[], period?: PeriodEnum } = {};

            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);

                // Migrate old filters: remove obsolete fields
                if (parsed.surveyIds) {
                    delete parsed.surveyIds;
                    parsed.cohortIds = undefined; // Migrate to "All Cohorts"
                }
                delete parsed.filterMode; // Remove obsolete field

                // For preset periods, use period enum instead of calculating dates
                if (parsed.periodType && parsed.periodType !== 'custom') {
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };

                    // Set period enum based on periodType
                    switch (parsed.periodType) {
                        case 'last30':
                            filters.period = PeriodEnum.THIRTY_DAYS;
                            break;
                        case 'last60':
                            filters.period = PeriodEnum.SIXTY_DAYS;
                            break;
                        case 'last90':
                            filters.period = PeriodEnum.NINETY_DAYS;
                            break;
                        case 'last120':
                            filters.period = PeriodEnum.ONE_TWENTY_DAYS;
                            break;
                        case 'last180':
                            filters.period = PeriodEnum.ONE_EIGHTY_DAYS;
                            break;
                        case 'last365':
                            filters.period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                            break;
                        case 'allTime':
                            filters.period = PeriodEnum.ALL_TIME;
                            break;
                        default:
                            filters.period = PeriodEnum.THIRTY_DAYS;
                    }
                    // Don't set dateFrom/dateTo for predefined periods
                } else if (parsed.periodType === 'custom' && parsed.dateFrom && parsed.dateTo) {
                    // Custom date range
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        period: PeriodEnum.DATE_RANGE,
                        dateFrom: parsed.dateFrom,
                        dateTo: parsed.dateTo,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                } else if (parsed.dateFrom && parsed.dateTo) {
                    // Direct date range (backward compatibility)
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        period: PeriodEnum.DATE_RANGE,
                        dateFrom: parsed.dateFrom,
                        dateTo: parsed.dateTo,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                } else {
                    // Fallback to brandIds if they exist
                    filters = {
                        brandIds: parsed.brandId ? [parsed.brandId] : undefined,
                        countryCodes: parsed.countryCodes?.length > 0 ? parsed.countryCodes : undefined
                    };
                }
            }

            // If no filters are saved or loaded, use defaults (last 30 days period)
            if (!filters.period && !filters.dateFrom && !filters.dateTo) {
                filters = {
                    brandIds: filters.brandIds || undefined, // Preserve brandIds if set
                    period: PeriodEnum.THIRTY_DAYS, // Use period enum instead of dates
                    cohortIds: undefined
                };
            }
            
            const result = await calculateBrandEquity({
                jwtToken: user.jwtToken,
                organisationId: user.organisation.id,
                payload: {
                    ...filters,
                    skipAiAnalysis: true // Skip AI summaries for statement sentiment page
                }
            }).unwrap();
            
            setBrandEquityData(result);
            
            // Cache the results
            localStorage.setItem(cacheKey, JSON.stringify({
                data: result,
                timestamp: Date.now()
            }));
            
        } catch (err: any) {
            console.error("Brand equity data loading failed:", err);
            
            // Handle subscription errors - redirect to billing page with upgrade modal
            if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
                const errorMessage = err?.data?.message || '';

                if (errorMessage && (errorMessage.includes('subscription') || errorMessage.includes('Professional'))) {
                    // Redirect to billing page with upgrade modal
                    navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
                    return;
                } else {
                    setError(err?.data?.message || intl.formatMessage({ id: 'statements.error.loading' }));
                }
            } else {
                setError(err?.data?.message || intl.formatMessage({ id: 'statements.error.loading' }));
            }
        } finally {
            setIsLoading(false);
        }
    }, [user, calculateBrandEquity, intl]);
    
    useEffect(() => {
        loadBrandEquityData();
    }, [loadBrandEquityData]);
    
    // Handle anchor scrolling after data loads
    useEffect(() => {
        if (!isLoading && brandEquityData && location.hash) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                const anchorId = location.hash.substring(1); // Remove the # prefix
                const element = document.getElementById(anchorId);
                if (element) {
                    element.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                    // Optional: Add visual highlight to the target element
                    element.style.animation = 'pulse 2s ease-in-out';
                }
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [isLoading, brandEquityData, location.hash]);

    // Get data summary information using the hook
    const dataSummary = useDataSummary({
        filterInfo,
        brandsData,
        cohortsData
    });

    const getMetricColor = (percentage: number, type: 'polarity' | 'intensity' | 'power' | 'controversy') => {
        if (type === 'controversy') {
            // Controversy: lower is better
            if (percentage <= 20) return '#237804'; // Dark green
            if (percentage <= 40) return '#52c41a'; // Light green
            if (percentage <= 60) return '#faad14'; // Yellow
            if (percentage <= 80) return '#fa8c16'; // Orange
            return '#f5222d'; // Red for 81-100
        }
        
        // Standardized colors for Score, Intensity, and Brand Power
        if (percentage < 0) return '#000000'; // Black for negative values (only Score)
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    const getPolarityColor = (percentage: number) => {
        // Use standardized color scheme for polarity
        if (percentage < 0) return '#000000'; // Black for negative values
        if (percentage === 0) return '#f5222d'; // Red for 0
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    // Function to sort categories
    const sortCategories = (categories: any[]) => {
        if (sortBy === 'default') {
            return [...categories].sort((a, b) => getDisplayName(a.category).localeCompare(getDisplayName(b.category)));
        }

        const [metric, direction] = sortBy.split('-') as [string, 'asc' | 'desc'];
        
        return [...categories].sort((a, b) => {
            const aValue = a[metric] ?? -Infinity;
            const bValue = b[metric] ?? -Infinity;
            
            if (direction === 'desc') {
                return bValue - aValue;
            }
            return aValue - bValue;
        });
    };

    // Get sorted categories for display
    const sortedCategories = brandEquityData ? sortCategories(brandEquityData.questionCategoryScores) : [];

    // Compute executive highlights from unlocked categories
    const highlights = useMemo(() => {
        if (!brandEquityData?.questionCategoryScores) return null;
        const unlocked = brandEquityData.questionCategoryScores.filter(
            (c: any) => !isCategoryLocked(c.category) && c.meanScore != null
        );
        if (unlocked.length === 0) return null;

        const strongest = unlocked.reduce((best: any, c: any) =>
            c.meanScore > best.meanScore ? c : best
        , unlocked[0]);

        const weakest = unlocked.reduce((worst: any, c: any) =>
            c.meanScore < worst.meanScore ? c : worst
        , unlocked[0]);

        const controversialCandidates = unlocked.filter((c: any) => c.meanControversyPercentage != null);
        const mostControversial = controversialCandidates.length > 0
            ? controversialCandidates.reduce((top: any, c: any) =>
                c.meanControversyPercentage > top.meanControversyPercentage ? c : top
            , controversialCandidates[0])
            : null;

        return { strongest, weakest, mostControversial };
    }, [brandEquityData, isCategoryLocked]);

    const sortOptions = [
        { value: 'default', label: 'Default (A → Z)', icon: null },
        { value: 'meanScore-desc', label: 'Score (High → Low)', icon: <Scale size="14px" /> },
        { value: 'meanScore-asc', label: 'Score (Low → High)', icon: <Scale size="14px" /> },
        { value: 'meanIntensityPercentage-desc', label: 'Intensity (High → Low)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanIntensityPercentage-asc', label: 'Intensity (Low → High)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanBrandPowerPercentage-desc', label: 'Brand Power (High → Low)', icon: <BicepsFlexed size="14px" /> },
        { value: 'meanBrandPowerPercentage-asc', label: 'Brand Power (Low → High)', icon: <BicepsFlexed size="14px" /> },
        { value: 'meanControversyPercentage-desc', label: 'Controversy (High → Low)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanControversyPercentage-asc', label: 'Controversy (Low → High)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
    ];
    
    const getMetricIcon = (type: 'polarity' | 'intensity' | 'power' | 'controversy', color?: string, isNull?: boolean) => {
        const iconColor = isNull ? '#bfbfbf' : color;
        const iconStyle = { color: iconColor, fontSize: 'inherit' };
        switch (type) {
            case 'polarity': return <Scale size="1em" color={iconColor} />;
            case 'intensity': return <ThunderboltOutlined style={iconStyle} />;
            case 'power': return <BicepsFlexed size="1em" color={iconColor} />;
            case 'controversy': return <WarningOutlined style={iconStyle} />;
            default: return null;
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { background-color: rgba(24, 144, 255, 0.1); }
                        50% { background-color: rgba(24, 144, 255, 0.3); }
                        100% { background-color: rgba(24, 144, 255, 0.1); }
                    }
                `}
            </style>
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb 
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                        { title: intl.formatMessage({ id: 'statements.breadcrumb.analytics' }) },
                        { title: intl.formatMessage({ id: 'statements.breadcrumb.statements' }) }
                    ]}
                />
            
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                            {intl.formatMessage({ id: 'statements.title' })}
                            <MaturityTag type="beta" />
                        </Title>
                        <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                            {intl.formatMessage({ id: 'statements.description' })}
                        </Text>
                        <MaturityAlert type="beta" style={{ marginTop: 16 }} />
                    </div>
                    {!isLoading && !error && brandEquityData && (
                        <Space size="large">
                            {/* Sort Controls */}
                            <Select
                                value={sortBy}
                                onChange={(value) => setSortBy(value)}
                                style={{ width: 270 }}
                                size="middle"
                                placeholder="Sort categories"
                                listHeight={400}
                            >
                                {sortOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        <Space size="small">
                                            {option.icon}
                                            {option.label}
                                        </Space>
                                    </Select.Option>
                                ))}
                            </Select>
                            
                            {/* View Mode Controls */}
                            <Button.Group>
                                <Button
                                    type={viewMode === 'cards' ? 'primary' : 'default'}
                                    icon={<AppstoreOutlined />}
                                    onClick={() => setViewMode('cards')}
                                >
                                    {intl.formatMessage({ id: 'statements.view.cards' })}
                                </Button>
                                <Button
                                    type={viewMode === 'table' ? 'primary' : 'default'}
                                    icon={<UnorderedListOutlined />}
                                    onClick={() => setViewMode('table')}
                                >
                                    {intl.formatMessage({ id: 'statements.view.table' })}
                                </Button>
                            </Button.Group>
                        </Space>
                    )}
                </div>

                {/* Data Summary */}
                {brandEquityData && (
                    <DataSummaryCard
                        brandName={dataSummary.brandName}
                        periodDescription={dataSummary.periodDescription}
                        cohortNames={dataSummary.cohortNames}
                        countryNames={dataSummary.countryNames}
                        totalResponses={brandEquityData.totalResponses}
                        calculatedAt={brandEquityData.calculatedAt}
                        changeFiltersLink="/dashboard/analytics/data-selection?returnTo=statements"
                    />
                )}

                {/* Executive Highlights Strip */}
                {brandEquityData && !brandEquityData.insufficientSampleSize && highlights && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24} md={8}>
                            <div style={{
                                background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                                border: '1px solid #b7eb8f',
                                borderRadius: 10,
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                height: '100%'
                            }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10,
                                    background: '#237804', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <TrophyOutlined style={{ color: '#fff', fontSize: 20 }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <Text style={{ fontSize: 12, color: '#389e0d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Strongest Element
                                    </Text>
                                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {getDisplayName(highlights.strongest.category)}
                                    </div>
                                    <Text style={{ fontSize: 13, color: '#237804', fontWeight: 600 }}>
                                        {Math.round(highlights.strongest.meanScore)}% Score
                                    </Text>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div style={{
                                background: 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)',
                                border: '1px solid #ffbb96',
                                borderRadius: 10,
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                height: '100%'
                            }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10,
                                    background: '#d4380d', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <ExclamationCircleOutlined style={{ color: '#fff', fontSize: 20 }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <Text style={{ fontSize: 12, color: '#d4380d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Needs Attention
                                    </Text>
                                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {getDisplayName(highlights.weakest.category)}
                                    </div>
                                    <Text style={{ fontSize: 13, color: '#d4380d', fontWeight: 600 }}>
                                        {Math.round(highlights.weakest.meanScore)}% Score
                                    </Text>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            {highlights.mostControversial ? (
                                <div style={{
                                    background: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)',
                                    border: '1px solid #ffd666',
                                    borderRadius: 10,
                                    padding: '16px 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                    height: '100%'
                                }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10,
                                        background: '#d48806', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                        <FireOutlined style={{ color: '#fff', fontSize: 20 }} />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <Text style={{ fontSize: 12, color: '#d48806', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Most Controversial
                                        </Text>
                                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {getDisplayName(highlights.mostControversial.category)}
                                        </div>
                                        <Text style={{ fontSize: 13, color: '#d48806', fontWeight: 600 }}>
                                            {Math.round(highlights.mostControversial.meanControversyPercentage)}% Controversy
                                        </Text>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    background: '#fafafa',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 10,
                                    padding: '16px 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                    height: '100%'
                                }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10,
                                        background: '#bfbfbf', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                        <FireOutlined style={{ color: '#fff', fontSize: 20 }} />
                                    </div>
                                    <div>
                                        <Text style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Most Controversial
                                        </Text>
                                        <div style={{ fontSize: 14, color: '#bfbfbf' }}>No data</div>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                )}

                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>{intl.formatMessage({ id: 'statements.loading' })}</p>
                    </div>
                )}
                
                {error && (
                    <Alert
                        message={intl.formatMessage({ id: 'statements.error.title' })}
                        description={error}
                        type="error"
                        closable
                        onClose={() => setError(null)}
                        style={{ marginBottom: 24 }}
                    />
                )}
                
                {!isLoading && !error && !brandEquityData && (
                    <Empty
                        description={intl.formatMessage({ id: 'statements.empty' })}
                        style={{ marginTop: 100 }}
                    />
                )}

                {!isLoading && !error && brandEquityData?.insufficientSampleSize && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 24px',
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%)',
                        borderRadius: 12,
                        border: '1px dashed #d9d9d9',
                        marginTop: 24
                    }}>
                        <CommentOutlined style={{ fontSize: 48, color: '#F59E0B', marginBottom: 16 }} />
                        <Title level={4} style={{ marginBottom: 8, color: '#1f2937' }}>
                            {intl.formatMessage({ id: 'statements.insufficientResponses.title' }, { defaultMessage: 'More Responses Needed' })}
                        </Title>
                        <Text style={{ color: '#6b7280', fontSize: 15, display: 'block', marginBottom: 16 }}>
                            {intl.formatMessage(
                                { id: 'statements.insufficientResponses.description' },
                                {
                                    defaultMessage: 'Statement sentiment analysis requires at least 10 survey responses. You currently have {count} response{plural}.',
                                    count: brandEquityData.totalResponses,
                                    plural: brandEquityData.totalResponses === 1 ? '' : 's'
                                }
                            )}
                        </Text>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            marginBottom: 16
                        }}>
                            <div style={{
                                width: 200,
                                height: 8,
                                background: '#f0f0f0',
                                borderRadius: 4,
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${Math.min((brandEquityData.totalResponses / 10) * 100, 100)}%`,
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)',
                                    borderRadius: 4,
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                            <Text style={{ color: '#8c8c8c', fontSize: 13 }}>
                                {brandEquityData.totalResponses}/10
                            </Text>
                        </div>
                        <Button
                            type="primary"
                            onClick={() => navigate('/dashboard/surveys')}
                            style={{
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                border: 'none'
                            }}
                        >
                            {intl.formatMessage({ id: 'statements.insufficientResponses.manageSurveys' }, { defaultMessage: 'Manage Surveys' })}
                        </Button>
                    </div>
                )}

                {!isLoading && !error && brandEquityData && !brandEquityData.insufficientSampleSize && (
                    <Collapse
                        bordered={false}
                        style={{ background: 'transparent' }}
                        expandIconPosition="start"
                        items={sortedCategories.map((category: any) => {
                            const locked = isCategoryLocked(category.category);
                            const stmtCount = category.input?.length || 0;

                            // Build score tag for header
                            let scoreTag: React.ReactNode = null;
                            if (!locked) {
                                if (sortBy !== 'default') {
                                    const [metric] = sortBy.split('-');
                                    const value = category[metric];
                                    if (value != null) {
                                        let metricName = '';
                                        let metricType: 'polarity' | 'intensity' | 'power' | 'controversy' = 'polarity';
                                        switch (metric) {
                                            case 'meanScore': metricName = 'Score'; metricType = 'polarity'; break;
                                            case 'meanIntensityPercentage': metricName = 'Intensity'; metricType = 'intensity'; break;
                                            case 'meanBrandPowerPercentage': metricName = 'Brand Power'; metricType = 'power'; break;
                                            case 'meanControversyPercentage': metricName = 'Controversy'; metricType = 'controversy'; break;
                                        }
                                        const displayValue = Math.round(value);
                                        const isNegative = value < 0;
                                        const color = (metricType === 'polarity' || metricType === 'power')
                                            ? getPolarityColor(value)
                                            : getMetricColor(value, metricType);
                                        scoreTag = (
                                            <Tag style={{
                                                fontSize: 13, padding: '2px 10px', fontWeight: 600,
                                                backgroundColor: color + '15', borderColor: color, color
                                            }}>
                                                {isNegative ? '' : '+'}{displayValue}% {metricName}
                                            </Tag>
                                        );
                                    }
                                } else if (category.meanScore !== null) {
                                    const color = getMetricColor(category.meanScore, 'intensity');
                                    scoreTag = (
                                        <Tag style={{
                                            fontSize: 13, padding: '2px 10px', fontWeight: 600,
                                            backgroundColor: color + '15', borderColor: color, color
                                        }}>
                                            {Math.round(category.meanScore)}%
                                        </Tag>
                                    );
                                }
                            }

                            return {
                                key: category.category,
                                label: (
                                    <div
                                        id={category.category.toLowerCase().replace(/\s+/g, '-')}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                                    >
                                        <span style={{ fontSize: 16, fontWeight: 600 }}>{getDisplayName(category.category)}</span>
                                        {locked && (
                                            <Tag color="default" style={{ fontSize: 12 }}>
                                                <LockOutlined style={{ marginRight: 4 }} />
                                                {intl.formatMessage({ id: 'subscription.upgrade.required' })}
                                            </Tag>
                                        )}
                                        {scoreTag}
                                        {!locked && (
                                            <Badge
                                                count={`${stmtCount} statement${stmtCount !== 1 ? 's' : ''}`}
                                                style={{
                                                    backgroundColor: '#f0f0f0',
                                                    color: '#595959',
                                                    fontSize: 12,
                                                    fontWeight: 400,
                                                    boxShadow: 'none'
                                                }}
                                            />
                                        )}
                                    </div>
                                ),
                                style: {
                                    marginBottom: 8,
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0'
                                },
                                children: locked ? (
                                    <div style={{
                                        background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.1) 0%, rgba(107, 114, 128, 0.15) 100%)',
                                        borderRadius: 12,
                                        border: '1px dashed #9ca3af',
                                        padding: '32px 24px',
                                        textAlign: 'center'
                                    }}>
                                        <LockOutlined style={{ fontSize: 32, color: '#9ca3af', marginBottom: 12 }} />
                                        <div style={{ marginBottom: 16 }}>
                                            <Text style={{ color: '#6b7280', fontSize: 14 }}>
                                                {intl.formatMessage({ id: 'subscription.upgrade.elementsRequireUpgradeToAccess' })}
                                            </Text>
                                        </div>
                                        <Button
                                            type="primary"
                                            icon={<CrownOutlined />}
                                            onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                                            style={{
                                                background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                                                border: 'none'
                                            }}
                                        >
                                            {intl.formatMessage({ id: 'subscription.upgrade.button' })}
                                        </Button>
                                    </div>
                                ) : stmtCount === 0 ? (
                                    <Empty
                                        description={intl.formatMessage({ id: 'statements.noStatements' })}
                                        style={{ marginBottom: 16 }}
                                    />
                                ) : viewMode === 'cards' ? (
                                    <Row gutter={[24, 24]} style={{ padding: '8px 0' }}>
                                        {category.input.map((statement: any) => {
                                            const anchorId = `${category.category.toLowerCase().replace(/\s+/g, '-')}-${statement.id}`;
                                            return (
                                                <Col xs={24} lg={12} xl={8} key={statement.id}>
                                                    <div id={anchorId}>
                                                        <StatementCard
                                                            statement={{
                                                                id: String(statement.id),
                                                                text: statement.text,
                                                                responseCount: statement.responseCount,
                                                                meanScore: statement.meanScore,
                                                                polarityPercentage: statement.meanScore,
                                                                intensityPercentage: statement.intensityPercentage,
                                                                brandPowerPercentage: statement.brandPowerPercentage,
                                                                controversyPercentage: statement.controversyPercentage,
                                                                scoreDistribution: statement.scoreDistribution,
                                                                questionTypeId: statement.questionTypeId,
                                                                scoringType: statement.scoringType
                                                            }}
                                                            isAdmin={false}
                                                            onResponseCountClick={handleResponseCountClick}
                                                        />
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                ) : (
                                    <Table
                                        dataSource={category.input.map((statement: any) => ({
                                            key: statement.id,
                                            id: statement.id,
                                            text: statement.text,
                                            responseCount: statement.responseCount,
                                            meanScore: statement.meanScore,
                                            polarityPercentage: statement.meanScore,
                                            intensityPercentage: statement.intensityPercentage,
                                            brandPowerPercentage: statement.brandPowerPercentage,
                                            controversyPercentage: statement.controversyPercentage,
                                            scoreDistribution: statement.scoreDistribution,
                                            questionTypeId: statement.questionTypeId,
                                            scoringType: statement.scoringType,
                                            anchorId: `${category.category.toLowerCase().replace(/\s+/g, '-')}-${statement.id}`
                                        }))}
                                        columns={[
                                            {
                                                title: 'ID',
                                                dataIndex: 'id',
                                                key: 'id',
                                                width: 60,
                                                render: (id: any, record: any) => (
                                                    <Tag color="blue" id={record.anchorId}>{id}</Tag>
                                                )
                                            },
                                            {
                                                title: intl.formatMessage({ id: 'statements.statementText' }),
                                                dataIndex: 'text',
                                                key: 'text',
                                                width: '30%'
                                            },
                                            {
                                                title: intl.formatMessage({ id: 'statements.responses' }),
                                                dataIndex: 'responseCount',
                                                key: 'responseCount',
                                                width: 100,
                                                align: 'center' as const,
                                                render: (responseCount: any, record: any) => (
                                                    <Tag
                                                        color="geekblue"
                                                        style={{
                                                            cursor: responseCount > 0 ? 'pointer' : 'default',
                                                            fontSize: '12px'
                                                        }}
                                                        onClick={() => {
                                                            if (responseCount > 0) {
                                                                handleResponseCountClick({
                                                                    id: String(record.id),
                                                                    text: record.text,
                                                                    responseCount: record.responseCount,
                                                                    meanScore: record.meanScore,
                                                                    polarityPercentage: record.meanScore,
                                                                    intensityPercentage: record.intensityPercentage,
                                                                    brandPowerPercentage: record.brandPowerPercentage,
                                                                    controversyPercentage: record.controversyPercentage,
                                                                    scoreDistribution: record.scoreDistribution,
                                                                    questionTypeId: record.questionTypeId,
                                                                    scoringType: record.scoringType
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        {responseCount}
                                                    </Tag>
                                                )
                                            },
                                            {
                                                title: intl.formatMessage({ id: 'statements.meanScore' }),
                                                dataIndex: 'meanScore',
                                                key: 'meanScore',
                                                width: 120,
                                                align: 'center' as const,
                                                render: (value: any) => (
                                                    <span style={{ color: value === null ? '#bfbfbf' : undefined }}>
                                                        {value !== null ? `${value.toFixed(1)}%` : '--'}
                                                    </span>
                                                )
                                            },
                                            {
                                                title: (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div>{intl.formatMessage({ id: 'statements.score' })}</div>
                                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                            {intl.formatMessage({ id: 'statements.scale.bipolar' })}
                                                        </div>
                                                    </div>
                                                ),
                                                dataIndex: 'meanScore',
                                                key: 'polarity',
                                                width: 100,
                                                align: 'center' as const,
                                                render: (value: any) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                        {getMetricIcon('polarity', value === null ? '#bfbfbf' : getMetricColor(value, 'polarity'), value === null)}
                                                        <span style={{ color: value === null ? '#bfbfbf' : undefined }}>
                                                            {value !== null ? `${value}%` : '--'}
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                title: (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div>{intl.formatMessage({ id: 'statements.intensity' })}</div>
                                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                            {intl.formatMessage({ id: 'statements.scale.positive' })}
                                                        </div>
                                                    </div>
                                                ),
                                                dataIndex: 'intensityPercentage',
                                                key: 'intensity',
                                                width: 100,
                                                align: 'center' as const,
                                                render: (value: any) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                        {getMetricIcon('intensity', value === null ? '#bfbfbf' : getMetricColor(value, 'intensity'), value === null)}
                                                        <span style={{ color: value === null ? '#bfbfbf' : undefined }}>
                                                            {value !== null ? `${value}%` : '--'}
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                title: (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div>{intl.formatMessage({ id: 'statements.brandPower' })}</div>
                                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                            {intl.formatMessage({ id: 'statements.scale.positive' })}
                                                        </div>
                                                    </div>
                                                ),
                                                dataIndex: 'brandPowerPercentage',
                                                key: 'brandPower',
                                                width: 100,
                                                align: 'center' as const,
                                                render: (value: any) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                        {getMetricIcon('power', value === null ? '#bfbfbf' : getMetricColor(value, 'power'), value === null)}
                                                        <span style={{ color: value === null ? '#bfbfbf' : undefined }}>
                                                            {value !== null ? `${value}%` : '--'}
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                title: (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div>{intl.formatMessage({ id: 'statements.controversy' })}</div>
                                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                            {intl.formatMessage({ id: 'statements.scale.positive' })}
                                                        </div>
                                                    </div>
                                                ),
                                                dataIndex: 'controversyPercentage',
                                                key: 'controversy',
                                                width: 100,
                                                align: 'center' as const,
                                                render: (value: any) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                        {getMetricIcon('controversy', value === null ? '#bfbfbf' : getMetricColor(Math.abs(value), 'controversy'), value === null)}
                                                        <span style={{ color: value === null ? '#bfbfbf' : undefined }}>
                                                            {value !== null ? `${value}%` : '--'}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                        ]}
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                    />
                                )
                            };
                        })}
                    />
                )}
            </div>
        </Content>
        
        {/* Score Distribution Modal */}
        <ScoreDistributionModal
            visible={scoreModalVisible}
            onClose={handleCloseScoreModal}
            statementText={selectedStatement?.text || ''}
            responseCount={selectedStatement?.responseCount || 0}
            scoreDistribution={selectedStatement?.scoreDistribution || null}
            questionType={selectedStatement?.scoringType === 'likert7_with_na' ? 'likert7_with_na' : 'likert7'}
        />
        </>
    );
}

export default StatementsPage;